<template>
    <div class="nested-segment-rule">
        <template v-if="!isLocaleEnglish">
            <div class="flex-break" />
            <strong>{{ $t('Criteria') }}</strong>
            <div class="flex-break" />
        </template>
        <simple-select
            :options="operatorOptions"
            :value="operatorValue"
            :check-valid-rule="checkValidRule"
            :disabled="readOnly"
            preselect-first
            field="operator"
            @input="$emit('input', $event)" />
        <simple-select
            v-if="resources.length"
            :options="segmentOptions"
            :value="segmentValue"
            :disabled="readOnly"
            :check-valid-rule="checkValidRule"
            :placeholder="$t('nestedSegmentRule.Select a saved segment')"
            field="segmentId"
            :min-menu-width="responsiveMinMenuWidth"
            :max-menu-width="responsiveMaxMenuWidth"
            :max-menu-height="400"
            @input="$emit('input', $event)"
            @highlighted-option-change="onHighlightedOptionChange">
            <template #option="{ option, index }">
                <pendo-multiselect-option
                    v-pendo-tooltip="{
                        content: option.tooltipContent,
                        delay: { show: 500, hide: 100 },
                        placement: 'top',
                        arrow: true,
                        multiline: true,
                    }"
                    :option="option"
                    :index="index" />
            </template>
            <template #aside>
                <segment-hover-card
                    v-if="showHoverCard"
                    :segment-id="highlightedOptionId"
                    :segment-dependents="highlightedSegmentDependents"
                    :show-builder-actions="false"
                    height="400px" />
            </template>
        </simple-select>
        <div
            v-if="segmentValue"
            class="segment-summary">
            <pendo-collapse
                v-model="activePanel"
                :panels="panels">
                <template #rulesTitle>
                    <pendo-router-link
                        :to="{ name: 'Segments', query: { segment: segmentValue.segmentId } }"
                        class="segment-link"
                        target="_blank">
                        <pendo-icon
                            class="segment-link-icon"
                            type="link"
                            size="12" />
                        <span class="segment-link-text">{{ segmentValue.name }}</span>
                    </pendo-router-link>
                    <span class="rule-count">{{ ruleCountText }}</span>
                </template>
                <template #rules>
                    <segment-rules-list
                        :segment="segmentValue"
                        :rules="segmentValue.definition.filters"
                        :rule-count="ruleCount"
                        :show-anonymous-visitor-data="showAnonymousVisitorData"
                        :show-title="false" />
                </template>
            </pendo-collapse>
        </div>
    </div>
</template>

<script>
import { useWindowSize } from '@vueuse/core';
import { PendoCollapse, PendoIcon, PendoTooltip, PendoMultiselectOption } from '@pendo/components';
import PendoRouterLink from '@/vue-router/pendo-router-link.vue';
import SegmentRulesList from '@/guides/p2-guide-details/guide-settings/segment-card/segment-rules-list.vue';
import SimpleSelect from '@/segments/components/segment-editor-vue/components/simple-select.vue';
import SegmentHoverCard from '@/common/hover-card/segment-hover-card/segment-hover-card.vue';
import { getOperatorOptions } from '@/segments/components/segment-editor-vue/segment-editor.utils';
import { mapGetters, mapState } from 'vuex';
import { PRODUCT_LINES } from '@/settings/subscription-settings/product-lines';
import { getResponsiveMenuWidth, shouldShowAsidePanel as shouldShowAsidePanelUtil } from '@/utils/responsive-multiselect.utils';

export default {
    name: 'NestedSegmentRule',
    components: {
        PendoCollapse,
        PendoIcon,
        PendoMultiselectOption,
        PendoRouterLink,
        SegmentHoverCard,
        SegmentRulesList,
        SimpleSelect,
    },
    directives: {
        PendoTooltip,
    },
    inject: ['onlyAllowGuideCompatibleRules'],
    props: {
        rule: {
            type: Object,
            required: true,
        },
        resources: {
            type: Array,
            required: true,
        },
        checkValidRule: {
            type: Boolean,
            default: false,
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
        segment: {
            type: Object,
            required: true,
        },
        isLocaleEnglish: {
            type: Boolean,
            default: true,
        },
    },
    emits: ['input'],
    setup () {
        const { width: windowWidth } = useWindowSize();

        return {
            windowWidth,
        };
    },
    data () {
        return {
            activePanel: null,
            panels: [
                {
                    name: 'rules',
                    title: 'rules',
                    titleSlotName: 'rulesTitle',
                },
            ],
            highlightedOptionId: null,
        };
    },
    computed: {
        ...mapState({
            segmentDependents: (state) => state.segments.segmentDependents,
        }),
        ...mapGetters({
            getSegmentGuideEligibility: 'segments/getSegmentGuideEligibility',
            getFeatureFlag: 'subscription/getFeatureFlag',
        }),
        operatorOptions () {
            return getOperatorOptions({ schema: this.rule.schema, id: this.rule.id });
        },
        operatorValue () {
            return this.operatorOptions.find((option) => option.operator === this.rule.operator);
        },
        ruleCount () {
            const { filters } = this.segmentValue?.definition || {};
            if (!filters) return 0;

            return filters.reduce((count, filter) => count + (filter.or?.length ?? 1), 0);
        },
        ruleCountText () {
            return this.$t('nestedSegmentRule.includesRuleCount', { ruleCount: this.ruleCount });
        },
        segmentOptions () {
            return this.resources
                .filter((segment) => segment.id !== this.segment?.id)
                .map((segment) => ({
                    name: segment.name,
                    segmentId: segment.id,
                    definition: segment.definition,
                    ...this.shouldDisableSegmentOption(segment),
                }));
        },
        showAnonymousVisitorData () {
            return this.getFeatureFlag('showAnonymous');
        },
        segmentValue () {
            return this.segmentOptions.find((option) => option.segmentId === this.rule.segmentId);
        },
        _onlyAllowGuideCompatibleRules () {
            return this.onlyAllowGuideCompatibleRules();
        },
        highlightedSegmentDependents () {
            return this.segmentDependents[this.highlightedOptionId] || {};
        },
        responsiveMinMenuWidth () {
            if (!this.showHoverCard) return;

            const responsiveWidth = getResponsiveMenuWidth(this.windowWidth);

            return responsiveWidth;
        },
        responsiveMaxMenuWidth () {
            if (!this.showHoverCard) return;

            const responsiveWidth = getResponsiveMenuWidth(this.windowWidth);

            return responsiveWidth;
        },
        showHoverCard () {
            const isAEU = this.$productLine(PRODUCT_LINES.ADOPT);
            const hasResults = this.segmentOptions.length > 0;

            return !isAEU && !!this.highlightedOptionId && hasResults && shouldShowAsidePanelUtil(this.windowWidth);
        },
    },
    methods: {
        shouldDisableSegmentOption (segment) {
            let tooltipContent = '';
            if (!segment.shared) {
                tooltipContent = this.$t('nestedSegmentRule.Segment cannot be selected: visibility set to only me');
            } else if (segment.compound) {
                tooltipContent = this.$t(
                    'nestedSegmentRule.Segment cannot be selected: Segment is already referenced by another segment',
                );
            } else if (this._onlyAllowGuideCompatibleRules) {
                const eligibilityInfo = this.getSegmentGuideEligibility(segment);
                if (!eligibilityInfo.eligible) {
                    tooltipContent = eligibilityInfo.reason;
                }
            }

            return { disabled: !!tooltipContent, tooltipContent };
        },
        onHighlightedOptionChange ($event) {
            if (!$event) return;

            this.highlightedOptionId = $event.segmentId;
        },
    },
};
</script>

<style scoped lang="less">
.nested-segment-rule {
    display: contents;
}

.segment-summary {
    flex-basis: 100%;
}

.hidden-options-text {
    color: var(--palette-gray-60);
    margin: 0;
    padding: 12px 16px;
}

:deep(.pendo-collapse) {
    border: none;
}

:deep(.pendo-collapse-item__header) {
    padding: 0;
    border: 0;
    height: 32px;
    line-height: 32px;
    grid-gap: 4px;
    justify-content: start;

    .segment-link {
        display: flex;
        align-items: center;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .segment-link-icon {
        margin-right: 5px;
    }

    .segment-link-text {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .rule-count {
        color: var(--palette-gray-70);
        white-space: nowrap;
    }
}

:deep(.pendo-collapse-item__content) {
    padding-top: 12px;
}

:deep(.pendo-collapse-item > [role='tab']) {
    width: fit-content;
}

:deep(.pendo-collapse-item__suffix-icon > svg) {
    stroke: var(--palette-gray-100);
}

.flex-break {
    /* Reliable flex line break - forces next items to new line */
    flex-basis: 100%;
    height: 0;
    width: 0;
    overflow: hidden;
}
</style>
